// ============================================================
//  StorePayments.js — the owner's own PocketFi, managed from the Store
//  • status at a glance: are the details accepted, is the webhook working,
//    can customers pay right now
//  • update Business ID / keys in place (empty key = keep the saved one),
//    checked with PocketFi before saving
//  • webhook link + where to paste it, recent payments, "check payments now"
//  • pay on delivery, help, disconnect / reconnect
//  Money goes straight to the owner's PocketFi — CommandKit only makes the
//  pay link and listens for PocketFi's confirmation.
// ============================================================
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { storeAction } from '../../api/client';
import { naira, ago, Switch } from './storeUi';

const errMsg = (e, d) => e?.response?.data?.message || d;

export default function StorePayments({ bId, store, payments, onStore, onPayments, onStats }) {
  const p = payments || {};
  const cfg = p.config;
  const connected = !!p.connected;
  const saved = !!(p.saved ?? cfg);
  const [editing, setEditing] = useState(!saved);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);         // { ok, message, link } — shown under the details
  const [confirmOff, setConfirmOff] = useState(false);

  const run = async (key, fn) => { setBusy(key); try { await fn(); } finally { setBusy(''); } };
  const apply = r => { if (r.data.payments) onPayments(r.data.payments); };

  const check = () => run('check', async () => {
    try { const r = await storeAction('pf_check', bId); apply(r); setResult({ ok: true, warn: !!r.data.warn, message: r.data.message, link: r.data.link }); r.data.warn ? toast(r.data.message, { icon: '⚠️' }) : toast.success('PocketFi accepted your details'); }
    catch (e) { setResult({ ok: false, message: errMsg(e, 'PocketFi didn’t accept your details') }); onPayments({ ...p, check: { ok: false, message: errMsg(e, ''), ago: 0 } }); }
  });
  const pending = () => run('pending', async () => {
    try { const r = await storeAction('pf_check_pending', bId); apply(r); if (r.data.stats) onStats(r.data.stats); toast.success(r.data.message); }
    catch (e) { toast.error(errMsg(e, 'Could not check payments')); }
  });
  const reconnect = () => run('on', async () => {
    try { const r = await storeAction('pf_reconnect', bId); apply(r); setEditing(false); toast.success(r.data.message); }
    catch (e) { toast.error(errMsg(e, 'Could not switch it back on')); setEditing(true); }
  });
  const disconnect = () => {
    if (!confirmOff) { setConfirmOff(true); return; }
    run('off', async () => {
      try { const r = await storeAction('pf_disconnect', bId); apply(r); toast.success(r.data.message); setResult(null); }
      catch (e) { toast.error('Could not disconnect'); }
      setConfirmOff(false);
    });
  };
  async function setCod(on) {
    try { const r = await storeAction('settings', bId, { payOnDelivery: on }); onStore(r.data.store); onPayments({ ...p, payOnDelivery: on }); toast.success(on ? 'Pay on delivery is on' : 'Pay on delivery is off'); }
    catch (e) { toast.error('Could not save'); }
  }
  const copy = (t, what = 'Copied') => { try { navigator.clipboard.writeText(t); toast.success(what); } catch { toast.error('Copy it manually'); } };

  // ── overall state ─────────────────────────────────────────
  const checkOk = p.check?.ok;
  const checkWarn = !!p.check?.warn;
  const hooked = p.lastWebhookAgo != null;
  const state = !saved ? 'new' : !connected ? 'off' : checkOk === false ? 'bad' : 'on';
  const hero = {
    new: ['Get paid straight into your PocketFi', 'Connect your PocketFi business account and customers get a secure Pay button at checkout — bank transfer, card or USSD. The money lands in your account, not ours.', 'warn'],
    off: ['PocketFi is switched off', 'Your details are still saved. While it’s off, customers can only use pay on delivery' + (store.payOnDelivery ? '.' : ' — which is also off, so nobody can order.'), 'muted'],
    bad: ['PocketFi isn’t accepting your details', 'Customers can’t pay online until this is fixed. Update your details below — usually the API key was changed or removed in PocketFi.', 'bad'],
    on:  ['Customers can pay you online', 'Every order gets its own PocketFi pay link in WhatsApp. When PocketFi confirms the payment, the order turns paid and the customer gets a receipt.', 'ok'],
  }[state];
  const pill = { new: ['Not connected', 'warn'], off: ['Switched off', 'muted'], bad: ['Needs attention', 'bad'], on: ['Connected', 'ok'] }[state];
  const st = p.stats || {};

  return (
    <div className="stp-col stp-pf">
      {/* ── 1. status ───────────────────────────────────────── */}
      <section className={`stp-card stp-pf-hero is-${hero[2]}`} aria-labelledby="stp-pf-h">
        <div className="stp-pf-hero-top">
          <span className="stp-pf-logo" aria-hidden="true"><i className="fa-solid fa-wallet" /></span>
          <div className="stp-pf-hero-txt">
            <span className={`stp-pill is-${pill[1]}`}>{pill[0]}</span>
            <h2 id="stp-pf-h">{hero[0]}</h2>
            <p>{hero[1]}</p>
          </div>
        </div>
        {state === 'new' && !editing && <button type="button" className="stp-btn stp-btn-brand" onClick={() => setEditing(true)}><i className="fa-solid fa-link" /> Connect PocketFi</button>}
        {state === 'off' && (
          <div className="stp-actions">
            <button type="button" className="stp-btn stp-btn-brand" onClick={reconnect} disabled={busy === 'on'}>{busy === 'on' ? <span className="stp-spin" /> : <i className="fa-solid fa-power-off" />} Switch back on</button>
            <button type="button" className="stp-btn stp-btn-ghost" onClick={() => setEditing(true)}>Use different details</button>
          </div>
        )}
        {saved && (
          <ul className="stp-health" aria-label="PocketFi health check">
            <Health tone={!connected ? 'muted' : checkWarn ? 'warn' : checkOk === true ? 'ok' : checkOk === false ? 'bad' : 'warn'} title="Your PocketFi details"
              text={!connected ? 'Switched off' : checkWarn ? `Keys accepted, but PocketFi couldn’t create a test checkout · checked ${ago(p.check.ago)}` : checkOk === true ? `Accepted by PocketFi · checked ${ago(p.check.ago)}` : checkOk === false ? (p.check.message || 'Rejected by PocketFi') : 'Not checked yet'}
              action={connected && <button type="button" className="stp-link" onClick={check} disabled={busy === 'check'}>{busy === 'check' ? 'Checking…' : 'Check now'}</button>} />
            <Health tone={hooked ? 'ok' : 'warn'} title="Payment notifications (webhook)"
              text={hooked ? `Working · last notification ${ago(p.lastWebhookAgo)}` : 'No notification from PocketFi yet. Paste the webhook link below — until then we still check unpaid orders every 5 minutes.'}
              action={!hooked && <a className="stp-link" href="#stp-hook">Set it up</a>} />
            <Health tone={connected || store.payOnDelivery ? 'ok' : 'bad'} title="Customers can pay"
              text={[connected && 'Pay button in WhatsApp and on your store page', store.payOnDelivery && 'pay on delivery'].filter(Boolean).join(' · ') || 'No way to pay is switched on — customers can’t order'} />
          </ul>
        )}
      </section>

      {/* ── 2. money ────────────────────────────────────────── */}
      {saved && (
        <div className="stp-pf-stats">
          <div className="stp-pf-stat"><span>Paid with PocketFi this month</span><b>{naira(st.monthAmount)}</b><small>{st.monthCount || 0} payment{st.monthCount === 1 ? '' : 's'}</small></div>
          <div className="stp-pf-stat"><span>Waiting for payment</span><b>{naira(st.waitingAmount)}</b>
            <small>{st.waitingCount || 0} order{st.waitingCount === 1 ? '' : 's'}{st.waitingCount > 0 && connected && <> · <button type="button" className="stp-link" onClick={pending} disabled={busy === 'pending'}>{busy === 'pending' ? 'Checking…' : 'Check now'}</button></>}</small></div>
          <div className="stp-pf-stat is-dark"><span>All time with PocketFi</span><b>{naira(st.allAmount)}</b><small>{st.allCount || 0} payment{st.allCount === 1 ? '' : 's'}</small></div>
        </div>
      )}

      <div className="stp-pf-grid">
        {/* ── 3. account details ──────────────────────────── */}
        {(saved || editing) && (
          <section className="stp-card" aria-labelledby="stp-pf-acc">
            <div className="stp-card-head">
              <div><h2 id="stp-pf-acc">Account details</h2><p className="stp-muted">From PocketFi → Settings → API Keys.</p></div>
              {saved && !editing && <button type="button" className="stp-btn stp-btn-ghost stp-btn-sm" onClick={() => { setEditing(true); setResult(null); }}><i className="fa-solid fa-pen" /> Update details</button>}
            </div>
            {editing ? (
              <Editor bId={bId} cfg={cfg} saved={saved} onCancel={saved ? () => setEditing(false) : null}
                onSaved={(payments, link, message, warn) => { onPayments(payments); setEditing(false); setResult({ ok: true, warn, message, link }); }} />
            ) : (
              <>
                <dl className="stp-pf-kv">
                  <div><dt>Business ID</dt><dd><b>{cfg.businessId}</b><button type="button" className="stp-icon-sm" aria-label="Copy Business ID" onClick={() => copy(cfg.businessId)}><i className="fa-regular fa-copy" /></button></dd></div>
                  <div><dt>API key</dt><dd className="stp-mono">{cfg.apiKeyMasked}</dd></div>
                  <div><dt>Secret key</dt><dd><span className="stp-mono">••••••••••••</span> <small className="stp-muted">saved, encrypted</small></dd></div>
                  {cfg.updatedAt && <div><dt>Last changed</dt><dd>{new Date(cfg.updatedAt.replace(' ', 'T')).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' })}</dd></div>}
                </dl>
                {connected && (
                  <div className="stp-actions">
                    <button type="button" className="stp-btn stp-btn-ghost stp-btn-sm" onClick={check} disabled={busy === 'check'}>{busy === 'check' ? <span className="stp-spin" /> : <i className="fa-solid fa-shield-halved" />} Check with PocketFi</button>
                  </div>
                )}
              </>
            )}
            {result && !editing && (
              <div className={`stp-pf-result ${result.warn ? 'is-warn' : result.ok ? 'is-ok' : 'is-bad'}`} role="status">
                <i className={`fa-solid ${result.warn ? 'fa-triangle-exclamation' : result.ok ? 'fa-circle-check' : 'fa-circle-exclamation'}`} aria-hidden="true" />
                <span>{result.message}{result.ok && result.link && <> <a href={result.link} target="_blank" rel="noreferrer">See the checkout customers get</a> (you don’t need to pay it).</>}</span>
              </div>
            )}
          </section>
        )}

        {!saved && (
          <section className="stp-card stp-pf-how" aria-labelledby="stp-pf-how">
            <h2 id="stp-pf-how">How it works</h2>
            <ol>
              <li><b>Customer checks out in WhatsApp</b><span>They get a “Pay ₦…” button for that exact order.</span></li>
              <li><b>They pay on PocketFi</b><span>Bank transfer, card or USSD — straight into your PocketFi account.</span></li>
              <li><b>The order turns paid</b><span>The customer gets a receipt in WhatsApp, and you get an alert and an email.</span></li>
            </ol>
            <p className="stp-muted stp-small">CommandKit never holds your money. PocketFi’s own fees apply.</p>
          </section>
        )}

        {/* ── 4. webhook ──────────────────────────────────── */}
        {saved && cfg?.webhookUrl && (
          <section className="stp-card" id="stp-hook" aria-labelledby="stp-pf-wh">
            <div className="stp-card-head">
              <div><h2 id="stp-pf-wh">Webhook link</h2><p className="stp-muted">Tells us the second a customer pays, so the order turns paid straight away.</p></div>
              <span className={`stp-pill is-${hooked ? 'ok' : 'warn'}`}>{hooked ? 'Working' : 'Not seen yet'}</span>
            </div>
            <div className="stp-copy"><code>{cfg.webhookUrl}</code>
              <button type="button" className="stp-btn stp-btn-dark stp-btn-sm" onClick={() => copy(cfg.webhookUrl, 'Webhook link copied')}><i className="fa-regular fa-copy" /> Copy</button></div>
            <ol className="stp-pf-steps">
              <li>Copy the link above.</li>
              <li>Log in to your <b>PocketFi dashboard</b>.</li>
              <li>Go to <b>Settings → Webhooks</b> (sometimes “Developer” or “API settings”).</li>
              <li>Paste it as the webhook / callback URL and save.</li>
            </ol>
            <p className="stp-muted stp-small">The link stays the same when you update your keys — you only paste it once. {hooked ? `Last notification ${ago(p.lastWebhookAgo)}.` : 'It shows “Working” after your next paid order.'}</p>
          </section>
        )}
      </div>

      {/* ── 5. recent payments ─────────────────────────────── */}
      {saved && (
        <section className="stp-card" aria-labelledby="stp-pf-rec">
          <div className="stp-card-head"><div><h2 id="stp-pf-rec">Recent PocketFi payments</h2><p className="stp-muted">Match these with your PocketFi dashboard using the reference.</p></div></div>
          {(p.recent || []).length === 0 ? <p className="stp-muted">No payments yet. They’ll show here as soon as a customer pays.</p> : (
            <div className="stp-table-wrap">
              <table className="stp-table stp-table-sm">
                <thead><tr><th>Order</th><th>Customer</th><th>Amount</th><th>PocketFi reference</th><th>When</th></tr></thead>
                <tbody>{p.recent.map(r => (
                  <tr key={r.number + r.ref}><td><b>#{r.number}</b></td><td>{r.name}</td><td><b>{naira(r.amount)}</b></td>
                    <td><span className="stp-mono">{r.ref}</span> <button type="button" className="stp-icon-sm" aria-label={`Copy reference for order ${r.number}`} onClick={() => copy(r.ref)}><i className="fa-regular fa-copy" /></button></td>
                    <td>{ago(r.ago)}</td></tr>))}</tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ── 6. pay on delivery ─────────────────────────────── */}
      <section className="stp-card" aria-labelledby="stp-cod">
        <div className="stp-row"><span><b id="stp-cod">Pay on delivery</b><small>Customers can also choose to pay cash or transfer when the order arrives. You confirm it yourself.</small></span>
          <Switch on={store.payOnDelivery} onChange={setCod} label="Pay on delivery" /></div>
        {!connected && !store.payOnDelivery && <p className="stp-pf-warn"><i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> Customers can’t order right now — connect PocketFi or turn on pay on delivery.</p>}
      </section>

      {/* ── 7. help ────────────────────────────────────────── */}
      <section className="stp-card stp-pf-help" aria-labelledby="stp-pf-faq">
        <h2 id="stp-pf-faq">Questions</h2>
        <details><summary>Where do I find my Business ID and keys?</summary>
          <p>Log in to PocketFi and open <b>Settings → API Keys</b>. Your <b>Business ID</b> is the short number shown there; the <b>API key</b> (token) and <b>secret key</b> are next to it. Your business must be verified on PocketFi first.</p></details>
        <details><summary>Who holds the money?</summary>
          <p>You do. Payments go straight into your own PocketFi account. CommandKit never receives or holds customer money — we only create the pay link and listen for PocketFi’s confirmation. PocketFi’s own fees apply.</p></details>
        <details><summary>A customer says they paid, but the order still says unpaid</summary>
          <p>We ask PocketFi about unpaid orders every 5 minutes, and instantly when the webhook works. Tap <b>Check now</b> next to “Waiting for payment” to ask straight away. If it still isn’t paid, search the customer’s name in your PocketFi dashboard.</p></details>
        <details><summary>I changed my keys on PocketFi</summary>
          <p>Tap <b>Update details</b> and paste the new API key and secret key. Leave a key empty to keep the one already saved. We check them with PocketFi before saving, so a typo can’t break your checkout.</p></details>
        <details><summary>Can I move to a different PocketFi account?</summary>
          <p>Yes — update all three details with the new account’s. Orders already paid stay paid. Remember to paste the webhook link into the new account too.</p></details>
        <details><summary>Is my secret key safe?</summary>
          <p>It’s stored encrypted and never shown again — not even to you. It’s only used to confirm that payment notifications genuinely come from PocketFi.</p></details>
      </section>

      {/* ── 8. switch off ──────────────────────────────────── */}
      {connected && (
        <section className="stp-card stp-pf-danger" aria-labelledby="stp-pf-off">
          <div className="stp-row"><span><b id="stp-pf-off">Switch off PocketFi</b><small>Customers stop seeing the Pay button. Your details are kept, so you can switch it back on any time.</small></span>
            <button type="button" className={`stp-btn stp-btn-sm ${confirmOff ? 'stp-btn-dangerfill' : 'stp-btn-danger'}`} onBlur={() => setConfirmOff(false)} onClick={disconnect} disabled={busy === 'off'}>{busy === 'off' ? <span className="stp-spin" /> : null}{confirmOff ? 'Tap again to switch off' : 'Switch off'}</button>
          </div>
        </section>
      )}
    </div>
  );
}

function Health({ tone, title, text, action }) {
  const icon = { ok: 'fa-circle-check', warn: 'fa-circle-exclamation', bad: 'fa-circle-xmark', muted: 'fa-circle-minus' }[tone];
  return (
    <li className={`stp-health-row is-${tone}`}>
      <i className={`fa-solid ${icon}`} aria-hidden="true" />
      <span><b>{title}</b><small>{text}</small></span>
      {action || null}
    </li>
  );
}

/** Edit Business ID / keys. Empty key fields keep the saved keys. */
function Editor({ bId, cfg, saved, onCancel, onSaved }) {
  const [f, setF] = useState({ businessId: cfg?.businessId || '', apiKey: '', secretKey: '' });
  const [show, setShow] = useState(false);
  const [verify, setVerify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => { setF(x => ({ ...x, [k]: v })); setErr(''); };
  const changed = f.businessId.trim() !== (cfg?.businessId || '') || f.apiKey.trim() || f.secretKey.trim();
  const complete = f.businessId.trim() && (saved || (f.apiKey.trim() && f.secretKey.trim()));
  async function paste(k) {
    try { const t = (await navigator.clipboard.readText()).trim(); if (t) set(k, t); }
    catch { toast.error('Your browser blocked pasting — long-press the box and paste'); }
  }
  async function save() {
    setBusy(true); setErr('');
    try {
      const r = await storeAction('pf_save', bId, { businessId: f.businessId.trim(), apiKey: f.apiKey.trim(), secretKey: f.secretKey.trim(), skipCheck: !verify });
      r.data.warn ? toast(r.data.message, { icon: '⚠️', duration: 7000 }) : toast.success(r.data.message);
      onSaved(r.data.payments, r.data.link, r.data.message, !!r.data.warn);
    } catch (e) { setErr(errMsg(e, 'Could not save')); }
    setBusy(false);
  }
  const keepHint = saved ? 'Leave empty to keep the saved key' : '';
  return (
    <div className="stp-pf-edit">
      {!saved && (
        <ol className="stp-steps stp-steps-sm">
          <li><b>1</b><span>Log in to <b>PocketFi</b> and finish business verification</span></li>
          <li><b>2</b><span>Open <b>Settings → API Keys</b></span></li>
          <li><b>3</b><span>Copy your Business ID, API key and secret key into the boxes below</span></li>
        </ol>
      )}
      <label className="stp-field">Business ID
        <span className="stp-pf-in"><input value={f.businessId} onChange={e => set('businessId', e.target.value.replace(/\s/g, ''))} placeholder="e.g. 29492" autoComplete="off" inputMode="text" />
          <button type="button" className="stp-pf-paste" onClick={() => paste('businessId')}>Paste</button></span></label>
      <label className="stp-field">API key (token) {saved && <small>· now {cfg?.apiKeyMasked}</small>}
        <span className="stp-pf-in"><input value={f.apiKey} onChange={e => set('apiKey', e.target.value.trim())} placeholder={keepHint || '352|3r5ZnULMaBfK…'} autoComplete="off" spellCheck={false} className="stp-mono-in" />
          <button type="button" className="stp-pf-paste" onClick={() => paste('apiKey')}>Paste</button></span></label>
      <label className="stp-field">Secret key <small>· checks that payment notifications really come from PocketFi</small>
        <span className="stp-pf-in"><input type={show ? 'text' : 'password'} value={f.secretKey} onChange={e => set('secretKey', e.target.value.trim())} placeholder={keepHint || 'Your secret key'} autoComplete="new-password" spellCheck={false} className="stp-mono-in" />
          <button type="button" className="stp-pf-paste" onClick={() => setShow(s => !s)} aria-label={show ? 'Hide secret key' : 'Show secret key'}><i className={`fa-regular ${show ? 'fa-eye-slash' : 'fa-eye'}`} /></button>
          <button type="button" className="stp-pf-paste" onClick={() => paste('secretKey')}>Paste</button></span></label>
      <label className="stp-pf-check"><input type="checkbox" checked={verify} onChange={e => setVerify(e.target.checked)} />
        <span><b>Check with PocketFi before saving</b><small>We ask PocketFi for a ₦100 test checkout with these details. Nothing is charged.</small></span></label>
      {err && <div className="stp-pf-result is-bad" role="alert"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /><span>{err}</span></div>}
      <div className="stp-actions">
        <button type="button" className="stp-btn stp-btn-dark" onClick={save} disabled={busy || !complete || (saved && !changed)}>
          {busy ? <span className="stp-spin" /> : <i className="fa-solid fa-shield-halved" />} {busy ? (verify ? 'Checking with PocketFi…' : 'Saving…') : saved ? (verify ? 'Check & save' : 'Save') : (verify ? 'Check & connect' : 'Connect')}</button>
        {onCancel && <button type="button" className="stp-btn stp-btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>}
      </div>
    </div>
  );
}
